import { useEffect, useState } from 'react';
import {
  getVisionEventImageObjectUrl,
  SensorTimelineImage,
  SensorTimelineOven,
} from '../../api/client';
import { downloadObjectUrl } from '../../utils/download';

interface Props {
  image: SensorTimelineImage;
  oven: SensorTimelineOven;
  onClose: () => void;
}

type Kind = 'full' | 'roi';

const KIND_LABELS: Record<Kind, string> = { full: 'תמונה מלאה', roi: 'אזור הפולים' };

function fileNameFor(image: SensorTimelineImage, kind: Kind) {
  const stamp = image.captured_at.replace(/[:.]/g, '-');
  return `${image.device_id}_${stamp}_${kind}.jpg`;
}

export default function SensorImageLightbox({ image, oven, onClose }: Props) {
  const [urls, setUrls] = useState<Partial<Record<Kind, string>>>({});
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let disposed = false;
    const created: string[] = [];
    Promise.all([
      image.has_full_image ? getVisionEventImageObjectUrl(image.event_id, 'full') : Promise.resolve(undefined),
      image.has_roi_image ? getVisionEventImageObjectUrl(image.event_id, 'roi') : Promise.resolve(undefined),
    ]).then(([full, roi]) => {
      [full, roi].forEach(url => { if (url) created.push(url); });
      if (disposed) {
        created.forEach(URL.revokeObjectURL);
      } else {
        setUrls({ full, roi });
      }
    }).catch(() => { if (!disposed) setFailed(true); });
    return () => {
      disposed = true;
      created.forEach(URL.revokeObjectURL);
    };
  }, [image]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const available = (['full', 'roi'] as Kind[]).filter(kind => urls[kind]);

  const downloadAll = () => {
    available.forEach(kind => downloadObjectUrl(urls[kind]!, fileNameFor(image, kind)));
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="card max-w-5xl w-full max-h-full overflow-auto p-4"
        dir="rtl"
        onClick={event => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <div className="font-bold text-sm text-espresso-800">
              תנור {oven.machine_id} · {image.blend_name || 'ללא תערובת'}
            </div>
            <div className="text-xs text-espresso-500">
              {new Date(image.captured_at).toLocaleString('he-IL')} · {image.device_id}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {available.length > 1 && (
              <button type="button" onClick={downloadAll} className="btn-secondary py-1.5">
                הורד הכל
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="סגירה"
              className="px-2.5 py-1 rounded-lg text-espresso-600 hover:bg-espresso-100 text-lg leading-none"
            >
              ×
            </button>
          </div>
        </div>

        {failed ? (
          <div className="text-sm text-red-600 py-8 text-center">טעינת התמונות נכשלה</div>
        ) : available.length === 0 ? (
          <div className="text-sm text-espresso-400 py-8 text-center">טוען תמונות…</div>
        ) : (
          <div className={`grid gap-4 ${available.length > 1 ? 'md:grid-cols-2' : ''}`}>
            {available.map(kind => (
              <div key={kind} className="space-y-2">
                <img
                  src={urls[kind]}
                  alt={KIND_LABELS[kind]}
                  className="w-full max-h-[65vh] object-contain rounded-lg bg-cream-100"
                />
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-espresso-600">{KIND_LABELS[kind]}</span>
                  <button
                    type="button"
                    onClick={() => downloadObjectUrl(urls[kind]!, fileNameFor(image, kind))}
                    className="btn-primary py-1.5"
                  >
                    הורדה
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
